import {
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  PageContent,
  Subnavbar,
  Toolbar,
  f7ready,
} from 'framework7-react';
import {
  ComponentProps,
  MutableRefObject,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  forwardRef,
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';

import { useAppContext } from '@contexts/AppContext';
import { isEmpty } from 'lodash';

import { useAnalytics } from '@hooks/useAnalytics';

import { useManagedHistory } from '@router/index';

import { GagglLogo } from '../../../assets/GagglLogo';
import { ErrorBoundary } from '../ErrorBoundary';
import './Screen.scss';

interface ScreenHeaderProps {
  back?: {
    to: string;
    label?: string;
  };
  left?: ReactNode;
  title?: string | ReactElement;
  right?: ReactNode;
  subnavbar?: ReactElement;
  pageUrl?: string;
}

const ScreenHeader = memo((props: ScreenHeaderProps) => {
  const { backlinkUrl } = useManagedHistory();
  const navbarRef = useRef<{ el: HTMLElement | null }>(null);

  useLayoutEffect(() => {
    if (process.env.REACT_APP_ENV === 'dev' && !backlinkUrl) {
      requestAnimationFrame(() => {
        if (navbarRef.current?.el) {
          f7ready((f7) => {
            f7.navbar.show(navbarRef.current!.el);
          });
        }
      });
    }
  }, []);

  if (!isEmpty(props)) {
    const { left, title, right, subnavbar } = props;

    const back =
      backlinkUrl && !left
        ? {
            to: backlinkUrl,
            label: undefined,
          }
        : {
            to: props.back?.to,
            label: props.back?.label,
          };

    return (
      <Navbar ref={navbarRef}>
        {back.to && !left && (
          <NavLeft
            backLink={back?.label ?? true}
            backLinkUrl={back?.to}
            backLinkForce={!!back?.to}
          />
        )}
        {!back.to && left && <NavLeft>{left}</NavLeft>}
        {title && <NavTitle>{title}</NavTitle>}
        {right && <NavRight>{right}</NavRight>}
        {subnavbar && <Subnavbar>{subnavbar}</Subnavbar>}
      </Navbar>
    );
  }

  return <Navbar hidden />;

  // return (
  //   <div className="screen-header">
  //     <div className="screen-header-inner">
  //       <div className="screen-header-left">
  //         {back && (
  //           <Link to={back.to}>
  //             <FontAwesomeIcon name="chevron-left" />
  //             {back.label && <span>{back.label}</span>}
  //           </Link>
  //         )}
  //       </div>
  //       <div className="screen-header-title">{title && <h1>{title}</h1>}</div>
  //       <div className="screen-header-right">{right}</div>
  //     </div>
  //   </div>
  // );
});

const FORCE_TRACK = true;

const shouldTrackPageView = (page: any, isUserLoggedIn: boolean): boolean => {
  if (isUserLoggedIn || FORCE_TRACK) {
    return true;
  }

  if (page?.app?.routes && page?.route?.route?.path) {
    const { path } = page.route.route;

    if (path === '/noauth/series/:tagSlug') {
      const { userAgent } = navigator;
      const isLinux = userAgent.toLowerCase().includes('linux');
      const isAndroid = userAgent.toLowerCase().includes('android');
      if (isLinux && !isAndroid) {
        return false;
      }
    }

    const isValidPath = !!page.app.routes.find((route) => route.path === path);

    return isValidPath;
  }

  return true;
};

export const Screen = ({
  className = '',
  children,
  name: screenName,
  disableLocationPermissionGate = true,
  metaData = {},
  headerProps,
  bottomToolbar,
  hideToolbar,
  onReenter,
  onEnter,
}: {
  children: ReactNode;
  name: string;
  className?: string;
  metaData?: {
    title?: string;
    description?: string;
    image?: string;
  };
  headerProps?: ScreenHeaderProps;
  bottomToolbar?: ReactNode;
  hideToolbar?: boolean;
  disableLocationPermissionGate?: boolean;
  onReenter?: () => void;
  onEnter?: () => void;
}) => {
  const didInit = useRef(false);
  const { track } = useAnalytics();
  const { userIsLoggedIn, updateIsLocationPermissionGateDisabled } = useAppContext();

  return (
    <>
      <Page
        className={`screen ${headerProps ? 'has-header' : 'has-no-header'} ${className}`}
        pageContent={false}
        noToolbar={!!bottomToolbar || hideToolbar}
        onPageInit={() => {
          didInit.current = true;
        }}
        onPageBeforeIn={(page) => {
          requestAnimationFrame(() => {
            updateIsLocationPermissionGateDisabled(disableLocationPermissionGate);
          });

          if (shouldTrackPageView(page, userIsLoggedIn)) {
            track('Screen View', {
              url: page.route.url,
              'Screen Name': screenName,
            });
          }

          onEnter?.();
          if (didInit.current && onReenter) {
            onReenter();
          }
        }}
      >
        <Helmet>
          {metaData.title && <title>{metaData.title}</title>}
          {metaData.title && <meta property="og:title" content={metaData.title} />}

          {metaData.description && <meta name="description" content={metaData.description} />}
          {metaData.description && (
            <meta property="og:description" content={metaData.description} />
          )}

          <meta
            property="og:image"
            content={metaData.image ?? 'https://my.gaggl.app/assets/images/icon.png'}
          />
        </Helmet>
        <ScreenHeader {...headerProps} />
        {bottomToolbar}
        {children}
      </Page>
    </>
  );
};

interface ScreenContentProps extends PropsWithChildren {
  screenContentClassName?: string;
  maxWidth?: 425;
  onPullToRefresh?: () => Promise<any>;
  infiniteScroll?: {
    hasReachedEnd: boolean;
    onLoadMore: () => Promise<void>;
  };
}
Screen.Content = forwardRef<{ el: HTMLElement | null }, ScreenContentProps>(
  (
    { screenContentClassName, onPullToRefresh, infiniteScroll, maxWidth = 425, children },
    ref: any,
  ) => {
    const handlePullToRefresh = !!onPullToRefresh
      ? async (done: Function) => {
          await onPullToRefresh();
          done();
        }
      : undefined;

    const allowInfinite = useRef(!!infiniteScroll);
    const [showPreloader, setShowPreloader] = useState(!!infiniteScroll);
    const loadMore = !!infiniteScroll?.onLoadMore
      ? async () => {
          if (!allowInfinite.current) {
            return;
          }

          allowInfinite.current = false;

          await infiniteScroll?.onLoadMore();
          allowInfinite.current = true;
        }
      : undefined;

    useEffect(() => {
      if (infiniteScroll?.hasReachedEnd) {
        setShowPreloader(false);
      }
    }, [infiniteScroll?.hasReachedEnd]);

    return (
      <PageContent
        ref={ref}
        ptr={!!onPullToRefresh}
        ptrMousewheel={!!onPullToRefresh}
        onPtrRefresh={handlePullToRefresh}
        infinite={!!infiniteScroll?.onLoadMore}
        infiniteDistance={50}
        infinitePreloader={showPreloader || !infiniteScroll?.hasReachedEnd}
        onInfinite={loadMore}
      >
        <ErrorBoundary>
          <div className={`screen-content ${screenContentClassName}`}>
            {maxWidth ? (
              <div
                style={{
                  maxWidth,
                  minHeight: '100%',
                  marginLeft: 'auto',
                  width: '100%',
                  marginRight: 'auto',
                }}
              >
                {children}
              </div>
            ) : (
              children
            )}
          </div>
        </ErrorBoundary>
      </PageContent>
    );
  },
);

Screen.BottomToolbar = ({
  maxWidth,
  height,
  children,
}: PropsWithChildren<{ maxWidth?: 425; height?: number }>) => {
  const ref = useRef<{
    el: HTMLElement | null;
    hide: () => void;
    show: () => void;
  }>(null);

  useLayoutEffect(() => {
    if (ref.current) {
      const toolbarEl = ref.current.el as HTMLElement;

      if (height) {
        toolbarEl.style.setProperty('--f7-toolbar-height', `${height}px`);
      } else {
        toolbarEl.style.removeProperty('--f7-toolbar-height');
      }
    }
  }, [height]);

  return (
    <Toolbar ref={ref} className="screen-bottom-toolbar" bottom>
      {maxWidth ? (
        <div style={{ maxWidth, marginLeft: 'auto', width: '100%', marginRight: 'auto' }}>
          {children}
        </div>
      ) : (
        children
      )}
    </Toolbar>
  );
};

Screen.GagglTitle = () => {
  return (
    <div style={{ width: 60, transform: 'translateY(4px)' }}>
      <GagglLogo />
    </div>
  );
};
